import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { courrierService } from '../services/api';
import { queryKeys } from '../lib/react-query';

export function Dashboard() {
  // Récupérer les statistiques
  const { data: statistics, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.courriers.statistics(),
    queryFn: () => courrierService.getStatistics(),
  });

  // Récupérer les courriers récents
  const { data: recentCourriers, isLoading: courriersLoading } = useQuery({
    queryKey: queryKeys.courriers.list({ page: 1, limit: 5 }),
    queryFn: () => courrierService.getCourriers({}, 1, 5),
  });

  const StatCard = ({
    title,
    value,
    icon: Icon,
    color = 'blue'
  }: {
    title: string;
    value: string | number;
    icon: React.ElementType;
    color?: 'blue' | 'yellow' | 'red' | 'green' | 'gray';
  }) => {
    const palette = {
      blue:   { bg: 'bg-blue-100 dark:bg-blue-900/20',     icon: 'text-blue-600 dark:text-blue-400' },
      yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', icon: 'text-yellow-600 dark:text-yellow-400' },
      red:    { bg: 'bg-red-100 dark:bg-red-900/20',       icon: 'text-red-600 dark:text-red-400' },
      green:  { bg: 'bg-green-100 dark:bg-green-900/20',   icon: 'text-green-600 dark:text-green-400' },
      gray:   { bg: 'bg-gray-100 dark:bg-gray-900/20',     icon: 'text-gray-600 dark:text-gray-400' },
    } as const;
    const { bg, icon } = palette[color] ?? palette.blue;

    return (
      <Card variant="shadow" className="transition-colors duration-200">
        <CardContent className="p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`p-2 rounded-full ${bg}`}>
                <Icon className={`h-6 w-6 ${icon}`} />
              </div>
            </div>
            <div className="ml-4 w-0 flex-1">
              <dl>
                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                  {title}
                </dt>
                <dd className="text-lg font-bold text-gray-900 dark:text-white">
                  {value}
                </dd>
              </dl>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const PriorityCard = ({
    title,
    value,
    icon: Icon,
    color = 'blue',
    priorityKey
  }: {
    title: string;
    value: string | number;
    icon: React.ElementType;
    color?: 'blue' | 'yellow' | 'red' | 'green' | 'gray' | 'orange';
    priorityKey: string;
  }) => {
    const palette = {
      blue:   { bg: 'bg-blue-100 dark:bg-blue-900/20',     icon: 'text-blue-600 dark:text-blue-400', hover: 'hover:bg-blue-50 hover:border-blue-300' },
      yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/20', icon: 'text-yellow-600 dark:text-yellow-400', hover: 'hover:bg-yellow-50 hover:border-yellow-300' },
      red:    { bg: 'bg-red-100 dark:bg-red-900/20',       icon: 'text-red-600 dark:text-red-400', hover: 'hover:bg-red-50 hover:border-red-300' },
      green:  { bg: 'bg-green-100 dark:bg-green-900/20',   icon: 'text-green-600 dark:text-green-400', hover: 'hover:bg-green-50 hover:border-green-300' },
      gray:   { bg: 'bg-gray-100 dark:bg-gray-900/20',     icon: 'text-gray-600 dark:text-gray-400', hover: 'hover:bg-gray-50 hover:border-gray-300' },
      orange: { bg: 'bg-orange-100 dark:bg-orange-900/20', icon: 'text-orange-600 dark:text-orange-400', hover: 'hover:bg-orange-50 hover:border-orange-300' },
    } as const;
    const { bg, icon, hover } = palette[color] ?? palette.blue;

    return (
      <Link
        to={`/courriers?priority=${priorityKey}`}
        className={`block transition-all duration-200 ${hover}`}
      >
        <Card variant="shadow" className="transition-colors duration-200 cursor-pointer">
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`p-2 rounded-full ${bg}`}>
                  <Icon className={`h-6 w-6 ${icon}`} />
                </div>
              </div>
              <div className="ml-4 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
                    {title}
                  </dt>
                  <dd className="text-lg font-bold text-gray-900 dark:text-white">
                    {value}
                  </dd>
                </dl>
              </div>
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  const ProgressBar = ({
    value,
    max,
    color,
    label
  }: {
    value: number;
    max: number;
    color: string;
    label: string;
  }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    
    return (
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="font-medium text-gray-600 dark:text-gray-400 capitalize">
            {label}
          </span>
          <span className="text-gray-900 dark:text-white font-medium">
            {value}
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className={`h-2 rounded-full ${
              color === 'blue' ? 'bg-blue-500' :
              color === 'yellow' ? 'bg-yellow-500' :
              color === 'green' ? 'bg-green-500' :
              'bg-gray-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
      <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Tableau de Bord
            </h1>
            <p className="text-sm text-gray-600">
              Vue d'ensemble de l'activité documentaire
            </p>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total courriers"
              value={statistics?.total || 0}
              icon={() => (
                <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
            />

            <StatCard
              title="En validation"
              value={statistics?.byStatus?.envalidation || 0}
              icon={() => (
                <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              color="yellow"
            />

            <StatCard
              title="Activité récente"
              value={statistics?.recentActivity || 0}
              icon={() => (
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              )}
              color="green"
            />

            <StatCard
              title="Signés"
              value={statistics?.byStatus?.signe || 0}
              icon={() => (
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              color="green"
            />
          </div>

          {/* Cartes de priorité cliquables */}
          <div>
            <Card variant="shadow" title="Répartition par priorité">
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <PriorityCard
                    title="Urgent"
                    value={statistics?.byPriority?.urgent || 0}
                    icon={() => (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    )}
                    color="red"
                    priorityKey="urgente"
                  />
                  
                  <PriorityCard
                    title="Haute"
                    value={statistics?.byPriority?.high || 0}
                    icon={() => (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    color="orange"
                    priorityKey="haute"
                  />
                  
                  <PriorityCard
                    title="Normale"
                    value={statistics?.byPriority?.normal || 0}
                    icon={() => (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    )}
                    color="blue"
                    priorityKey="normale"
                  />
                  
                  <PriorityCard
                    title="Basse"
                    value={statistics?.byPriority?.low || 0}
                    icon={() => (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    color="gray"
                    priorityKey="basse"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Section principale avec courriers récents et visualisations */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Courriers récents */}
            <div className="lg:col-span-2">
              <Card
                variant="shadow"
                title="Courriers récents"
                actions={
                  <Link
                    to="/courriers"
                    className="text-sm font-medium text-blue-600 hover:text-blue-500 flex items-center space-x-1"
                  >
                    <span>Voir tout</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                }
              >
                <CardContent>
                  {courriersLoading ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      ))}
                    </div>
                  ) : recentCourriers?.data?.length ? (
                    <div className="space-y-4">
                      {recentCourriers.data.map((courrier) => (
                        <div
                          key={courrier.id}
                          className="flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {courrier.subject}
                            </p>
                            <p className="text-sm text-gray-500">
                              {courrier.sender} → {courrier.recipient}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                courrier.status === 'RECU' ? 'bg-blue-100 text-blue-800' :
                                courrier.status === 'EN_VALIDATION' ? 'bg-yellow-100 text-yellow-800' :
                                courrier.status === 'SIGNE' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {courrier.status === 'RECU' ? 'Reçu' :
                               courrier.status === 'EN_VALIDATION' ? 'En validation' :
                               courrier.status === 'SIGNE' ? 'Signé' : courrier.status}
                            </span>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                courrier.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                                courrier.priority === 'URGENT' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {courrier.priority === 'LOW' ? 'Basse' :
                               courrier.priority === 'NORMAL' ? 'Normale' :
                               courrier.priority === 'HIGH' ? 'Haute' : 'Urgente'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      Aucun courrier récent
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Visualisations par statut */}
            <div>
              <Card variant="shadow" title="Répartition par statut">
                <CardContent>
                  {statsLoading ? (
                    <div className="animate-pulse">
                      <div className="h-48 bg-gray-200 rounded"></div>
                    </div>
                  ) : statistics?.byStatus ? (
                    <div className="space-y-6">
                      <div className="space-y-4">
                        {Object.entries(statistics.byStatus).map(([status, count]) => {
                          // Formater les labels pour l'affichage
                          const formatLabel = (status: string) => {
                            switch (status) {
                              case 'recu': return 'Reçu';
                              case 'envalidation': return 'En validation';
                              case 'signe': return 'Signé';
                              case 'envoye': return 'Envoyé';
                              case 'rejete': return 'Rejeté';
                              default: return status;
                            }
                          };

                          return (
                            <ProgressBar
                              key={status}
                              value={count as number}
                              max={statistics.total}
                              color={
                                status === 'recu' ? 'blue' :
                                status === 'envalidation' ? 'yellow' :
                                status === 'signe' ? 'green' : 'gray'
                              }
                              label={formatLabel(status)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      Aucune donnée disponible
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
  );
}